import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import heroRobot from "@/assets/hero-robot.jpg";
import { useAuth } from "@/contexts/AuthContext";

const Hero = () => {
  const { t } = useTranslation();
  const { user, subscription } = useAuth();
  const navigate = useNavigate();
  const isPremium = subscription?.subscribed;

  const handleStartTrial = () => {
    if (user) {
      navigate('/pricing');
    } else {
      navigate('/auth');
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-20">
      {/* Glassmorphism orbs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-opal rounded-full blur-3xl opacity-20 animate-glow" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-opal rounded-full blur-3xl opacity-20 animate-glow" style={{ animationDelay: '1s' }} />
      
      {/* Hero content */}
      <div className="container relative z-10 px-4 py-8 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8 animate-slide-up">
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              {isPremium ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-primary rounded-full animate-pulse-subtle">
                  <Sparkles className="w-4 h-4 text-white animate-spin-slow" />
                  <span className="text-sm font-bold">✨ You're Premium — Full Access</span>
                </div>
              ) : (
                <>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-card rounded-full border border-primary/20 animate-pulse-subtle">
                    <Sparkles className="w-4 h-4 text-primary animate-spin-slow" />
                    <span className="text-sm font-medium">🔥 Limited Time: 7-Day Free Trial</span>
                  </div>
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-primary rounded-full hover:shadow-glow-primary transition-all duration-300 cursor-pointer hover:scale-105"
                  >
                    <span className="text-sm font-bold">💎 Premium Access — $99/mo</span>
                  </button>
                </>
              )}
            </div>
            
            <div className="space-y-4">
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                Elite Crypto Signals,<br />One Powerful Platform
              </h1>
              <p className="text-2xl md:text-3xl font-bold tracking-wider text-primary">
                Trade Like the Top 1%
              </p>
            </div>

            <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Stop losing money on guesswork. Get instant access to <span className="text-primary font-semibold">multiple elite professional traders</span> delivering high-conviction signals in real-time. Bitcoin, Ethereum, altcoins, and futures — all in one unified platform.
            </p>
            

            <div className="flex flex-col sm:flex-row gap-4">
              {isPremium ? (
                <>
                  <Button 
                    size="lg" 
                    className="bg-gradient-primary hover:shadow-glow-primary transition-all duration-300 text-lg px-8 py-6 group"
                    onClick={() => window.location.href = 'https://dashboard.oraculusx.com'}
                  >
                    Go to Dashboard
                    <Sparkles className="ml-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="secondary"
                    className="text-lg px-8 py-6 border border-primary/20 hover:border-primary/50 transition-all"
                    onClick={() => navigate('/pricing')}
                  >
                    Manage Subscription
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    size="lg" 
                    className="bg-gradient-primary hover:shadow-glow-primary transition-all duration-300 text-lg px-8 py-6 group"
                    onClick={handleStartTrial}
                  >
                    {user ? "Get Premium — Start Free Trial" : "Sign Up — Start Free Trial"}
                    <Sparkles className="ml-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="secondary"
                    className="text-lg px-8 py-6 border border-primary/20 hover:border-primary/50 transition-all"
                    asChild
                  >
                    <a href="/community" className="flex items-center gap-2">
                      Join Our Community
                      <span className="text-xs bg-primary/20 px-2 py-0.5 rounded-full">FREE</span>
                    </a>
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Right image */}
          <div className="relative">
            <div className="relative animate-float robot-glow-animated robot-pulse-glow">
              <img 
                src={heroRobot} 
                alt="Professional cryptocurrency trading signals platform - AI-powered Bitcoin and altcoin predictions dashboard"
                className="w-full h-auto rounded-lg relative z-10"
                fetchPriority="high"
                width="1920"
                height="1080"
              />
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;